// Los filtros de búsqueda de <Table> (components/Table.jsx) se persisten en sessionStorage
// bajo claves con este prefijo, para mantenerlos al navegar dentro de la misma pantalla.
export const TABLE_FILTERS_PREFIX = 'table-filters:';

// Evento de ventana con el que un <Table> avisa a los demás <Table> montados que usan la
// misma clave de filtros (p. ej. los tabs Todos/Activas/Autorizadas de una misma pantalla)
// que el usuario cambió la búsqueda, para que la apliquen sin tener que volver a capturarla.
export const TABLE_FILTERS_SYNC_EVENT = 'table-filters-sync';

// Clave de sessionStorage de los filtros de un <Table>. Con `storageId` explícito la clave no
// depende de las columnas (sirve para tabs que muestran columnas distintas pero deben compartir
// la búsqueda, p. ej. Plazas); sin él se usa el modelo de permisos + la firma de columnas.
export const buildTableFiltersKey = (pathname, { storageId, model, cols = [] } = {}) => {
  if (storageId) return `${TABLE_FILTERS_PREFIX}${pathname}:${storageId}`;
  const colsSignature = cols.map((col) => col.dataIndex || col.key).join('|');
  return `${TABLE_FILTERS_PREFIX}${pathname}:${model || 'default'}:${colsSignature}`;
};

// Búsqueda guardada de un <Table> como objeto `{ campo: valor }` (solo campos con valor), para
// que las vistas paginadas en servidor la incluyan al pedir los datos de otro tab.
export const readPersistedTableQuery = (pathname, options) => {
  try {
    const key = buildTableFiltersKey(pathname, options);
    // Si el <Table> ya guardó la consulta normalizada (importes sin "$", `searchKey` de cada
    // columna, etc.), se usa esa; si no, los filtros tal cual.
    const consulta = sessionStorage.getItem(`${key}:consulta`);
    if (consulta) {
      const parsed = JSON.parse(consulta);
      return Object.fromEntries(Object.entries(parsed).filter(([, v]) => v));
    }
    const raw = sessionStorage.getItem(key);
    const stored = raw ? JSON.parse(raw) : [];
    return Object.fromEntries(stored.filter((f) => f.value).map((f) => [f.prop, f.value]));
  } catch (err) {
    return {};
  }
};

const removeKeysStartingWith = (prefix) => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (err) {
    // ignora errores de almacenamiento (modo privado, cuota, etc.)
  }
};

// Se llama al cerrar sesión (manual o por expiración de token) para no dejar
// residuos de búsquedas (potencialmente datos sensibles de nómina/RH) en el navegador.
export const clearPersistedTableFilters = () => removeKeysStartingWith(TABLE_FILTERS_PREFIX);

// Se llama al cambiar de pantalla, para limpiar solo los filtros de la pantalla que se abandona.
export const clearPersistedTableFiltersForPath = (pathname) => (
  removeKeysStartingWith(`${TABLE_FILTERS_PREFIX}${pathname}:`)
);
